import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  styled,
  Tooltip,
  alpha
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import ListIcon from '@mui/icons-material/List';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    '& .iconWrapper': {
      transform: 'scale(1.1)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: 'linear-gradient(90deg, #5E35B1, #3F51B5)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
  transition: 'all 0.3s ease',
}));

interface StatsCardsProps {
  stats: {
    totalVolume: number;
    floorPrice: number;
    bestOffer: number | null;
    listedPercentage: number | null;
    numOwners: number;
    averagePriceChange: number;
    salesVolumeChange: number;
    liquidity: number;
    uniqueBuyers: number;
    uniqueSellers: number;
  };
  isLoading: boolean;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toFixed(2);
};

const formatPercentage = (num: number): string => {
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(1)}%`;
};

const StatsCards: React.FC<StatsCardsProps> = ({ stats, isLoading }) => {
  const cards = [
    {
      title: 'Общий объем',
      value: `${formatNumber(stats.totalVolume)} ETH`,
      icon: <TrendingUpIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Общий объем всех продаж этой коллекции',
      gradient: 'linear-gradient(90deg, #5E35B1, #3F51B5)',
    },
    {
      title: 'Минимальная цена',
      value: `${stats.floorPrice.toFixed(3)} ETH`,
      icon: <LocalOfferIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Минимальная цена NFT, в настоящее время выставленных на продажу',
      gradient: 'linear-gradient(90deg, #7E57C2, #5E35B1)',
    },
    {
      title: 'Владельцы',
      value: formatNumber(stats.numOwners),
      icon: <PeopleIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Количество уникальных адресов, владеющих NFT из этой коллекции',
      gradient: 'linear-gradient(90deg, #3F51B5, #2196F3)',
    },
    {
      title: 'Листинг',
      value: stats.listedPercentage !== null ? `${stats.listedPercentage.toFixed(1)}%` : 'N/A',
      icon: <ListIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Процент от общего количества NFT, выставленных на продажу',
      gradient: 'linear-gradient(90deg, #2196F3, #00BCD4)',
    },
    {
      title: 'Изменение цены',
      value: formatPercentage(stats.averagePriceChange),
      icon: <PriceChangeIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Изменение средней цены за последний период',
      gradient: 'linear-gradient(90deg, #00BCD4, #009688)',
    },
    {
      title: 'Изменение объема',
      value: formatPercentage(stats.salesVolumeChange),
      icon: <ShowChartIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Изменение объема продаж за последний период',
      gradient: 'linear-gradient(90deg, #009688, #4CAF50)',
    },
    {
      title: 'Ликвидность',
      value: `${stats.liquidity.toFixed(1)}%`,
      icon: <AccountBalanceIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Соотношение проданных NFT к общему количеству (показатель ликвидности)',
      gradient: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
    },
    {
      title: 'Уникальные покупатели',
      value: formatNumber(stats.uniqueBuyers),
      icon: <CompareArrowsIcon sx={{ color: 'white', fontSize: 28 }} />,
      tooltip: 'Количество уникальных адресов, покупающих NFT из этой коллекции',
      gradient: 'linear-gradient(90deg, #8BC34A, #CDDC39)',
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <StyledCard
            sx={{
              '&::before': {
                background: card.gradient,
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Tooltip title={card.tooltip} arrow placement="top">
                <IconWrapper className="iconWrapper" sx={{ 
                  background: card.gradient
                }}>
                  {card.icon}
                </IconWrapper>
              </Tooltip>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {card.title}
              </Typography>
              {isLoading ? (
                <Skeleton width={120} height={45} animation="wave" />
              ) : (
                <Typography variant="h4" fontWeight="700" sx={{ mt: 1 }}>
                  {card.value}
                </Typography>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards; 